import { useState, useEffect, useRef } from "react";
import cn from "classnames";
import { Helmet } from "react-helmet";
import API from "../../functions/api";
// import { gtmPageview, gtmPush } from '../../functions/gtm';
import Layout from "../../components/Layout";
import Button from "../../audi-ui-components/Button";
import queryString from "query-string";
import PostcodeModal from "./components/PostcodeModal";
import BudgetCalculatorComponent from "./components/BudgetCalculatorComponent";
import EnquiryForm from "./components/EnquiryForm";
import ApplyFormModal from "./components/ApplyFormModal";
import placeholderImg from "./components/silhouette_A4.png";
import { unFormatNumber } from "../../functions/textFormatting";
import { gtmPageview, gtmPush } from "../../functions/gtm";
import {
  UI_AUDI,
  DEFAULT_THEME
} from "../../constants";
import "./finance-calculator.scss";

const budgetCalcDisclaimer = `
<p class="mb-3">
  <b><sup>±</sup>Comparison Rate</b> - The represented range of interest rates that may be selected using this budget calculator is indicative only and does not reflect the actual rate offered to a particular customer. The actual rate will vary, depending on your circumstances, our credit criteria and other details. Conditions, fees and charges may apply. Comparison rate for the purpose of the National Credit Code is based on 5 year fixed rate secured loan of $30,000. WARNING: This comparison rate is true only for the examples given and may not include all fees and charges. Different terms, fees or other loan amounts might result in a different comparison rate. Please contact your Audi Dealer for more information.
</p>
<p class="mb-3">
  <b><sup>±</sup>Interest Rate</b> - The represented range of interest rates that may be selected using this budget calculator is indicative only and does not reflect the actual rate offered to a particular customer. The actual rate will vary, depending on your circumstances, our credit criteria and other details. Conditions, fees and charges may apply. Please contact your Audi Dealer for more information.
</p>
<p class="mb-3">
  <b><sup>#</sup>Weekly Repayment</b> - The repayment amount is indicative only and may not include provisions for fees and charges. Dealer may charge an administration fee to arrange finance, please contact your Audi Dealer for more information.
</p>
`;

const BudgetCalculator = ({ location }) => {

  const query = queryString.parse(location.search);
  const theme = query.theme || DEFAULT_THEME;
  
  const [locState, setLocState] = useState("");
  const [locPostcode, setLocPostcode] = useState("");
  const [hasTrackedPostcode, setHasTrackedPostcode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [financeValues, setFinanceValues] = useState(null);
  const [results, setResults] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [terms, setTerms] = useState(null);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    gtmPageview(location.pathname);
  }, []);
  
  useEffect(() => {
    clearTimeout(searchTimeout?.current);
    setError(null);
    if (!financeValues || financeValues === null) {
      setResults(null);
      setLoading(false);
    } else {
      let payload = {...financeValues};
      setLoading(true);
      setResults([{}, {}]);
      searchTimeout.current = setTimeout(()=>{
        API.post(
          `/1/afs/budgetcalculator`,
          payload
      ).then((response) => {
          // console.log(response.data);
          setResults(response.data);
          if (!response.data || response.data.length === 0) {
            setError("Sorry, we couldn't find any vehicles that matched your search.");
          }
          setLoading(false);
      }).catch((error) => {
          // console.error(error.response);
          setLoading(false);
          setResults([]);
          let msg = "An error has occured";
          if (error.response?.status === 404) { msg = "Sorry, we couldn't find any vehicles that matched your search."}
          setError(msg);
      });
      }, 3000);
    }
    return () => { clearTimeout(searchTimeout?.current); }
    // eslint-disable-next-line
  }, [financeValues]);

  useEffect(() => {
    if (!selectedResult?.driveAwayPrice) {
      setTerms(null);
    } else {
      let payload = {
        "product": {
          "id": financeValues.purpose,
          "parameter":[
            {
              "id": "FinanceProduct",
              "text": financeValues.purpose,
              "changed": true
            },
            {
              "id": "MaskRate",
              "text": financeValues.interestRate,
              "changed": true
            }
          ]
        },
        "vehicle": {
          "key": selectedResult.modelCode,
          // "year": engine.year,
          "priceTotal": unFormatNumber(selectedResult.driveAwayPrice),
          "priceModel": unFormatNumber(selectedResult.driveAwayPrice)
        },
        "domain": "AUDI.CC.AUS",
        "name": "Defaults"
      };
      API.post(
        `/1/afs/default`,
        payload
      ).then((response) => {
        let copy = {
          description: response.data?.summary?.disclaimer,
          productDisclaimer: response.data?.summary?.product?.disclaimer
        };
        setTerms(copy);
      }).catch((error) => {
        console.error(error);
      });
    }
  }, [selectedResult]);
  
  return (
    <Layout ui={query.ui ? query.ui : UI_AUDI} theme={query.theme}>
      <Helmet>
        <title>Budget Calculator | Audi Australia</title>
      </Helmet>

      <main className="audi-forms__page budget-calculator">
        
        {!showEnquiry && <div className="row mx-0 mx-medium-n1">

          <div className={cn("col-12 col-medium-7 px-1 order-first order-medium-last", {"d-none": loading || results !== null})}>
            <div className={`p-5 ${theme === "dark" ? "aui-color-gray10" : ""}`}>To get started, enter your desired weekly payment in the finance calculator <span className="d-none d-medium-inline">on the left</span><span className="d-medium-none">below</span>.</div>
          </div>
          
          <div className="col-12 col-medium-5 px-1" style={{ position: "relative", zIndex: "99" }}>
            <BudgetCalculatorComponent
              className={`${theme === "dark" ? "aui-color-gray10" : "aui-color-gray95"} p-3 p-large-5 me-medium-4`}
              theme={theme}
              onCalculateBudget={setFinanceValues}
              onReset={()=>{
                setFinanceValues(null);
                setResults(null);
                setError(null);
                setTimeout(()=>{ window.scrollTo(0,0); }, 500);
              }}
              disclaimer={budgetCalcDisclaimer}
            />
          </div>
          
          <div className={cn("col-12 col-medium-7 px-1 search-results__column", {"d-none": results === null && !loading})}>
            <div className="py-3 pt-large-5 mx-2 mx-medium-0">
              <div className="row mb-2">
                <div className="col-12 col-xsmall-6"><h2 className="aui-headline-3 mb-2">Vehicle matches</h2></div>
                <div className="col-12 col-xsmall-6 text-xsmall-end aui-small pt-xsmall-2">
                  Postcode: {locPostcode}. <button type="button" className="aui-textlink" onClick={()=>{ setLocState(""); setLocPostcode(""); gtmPush("fbcChangePostcodeClicked", "FinanceBudgetCalculator"); }}>Change?</button>
                </div>
              </div>
            </div>

            {error && <div className="aui-color-text-red p-5">{error}</div>}
            
            <div className="search-results__wrapper">
              <div className="search-results">
                <div className={cn("row mx-n1", {"loading": loading})}>
                  {results?.map((car,i) => {
                    return (
                      <div className="col-12 col-xsmall-6 col-medium-12 col-large-6 px-1 mb-2" key={`car${i}`}>
                        <div className={cn(`${theme === "dark" ? "aui-color-gray20" : "aui-color-gray95"} search-result`, {"placeholder": !car?.modelName})}>
                          <div className="top">
                            <div className="img-wrapper">
                              <img 
                                src={car?.imageUrl || placeholderImg} 
                                alt=""
                              />
                            </div>
                            <div className="text-center">
                              <p className="title">{car.variantName} <span className="engine">{car.engineName}</span></p>
                              <Button variant="text" theme={theme === "dark" ? "light" : null} href={car.exploreLink || "https://www.audi.com.au/en/audi-new-car-search"} rel="noreferrer" onClick={()=>{ gtmPush("fbcExploreVehicleClicked", "FinanceBudgetCalculator", car.variantName); }}>
                                Explore vehicle
                              </Button>
                            </div>
                          </div>
                          <div className="bottom">
                            <hr />
                            <table className="mb-4"><tbody>
                              <tr><td>Weekly repayments<sup>#</sup>:</td><td>{car.weeklyRepaymentsLabel}</td></tr>
                              <tr><td>Balloon Amount<sup>≠</sup></td><td>{car.finalBalloonRepayments || car.fianlBalloonRepayments}</td></tr>
                              <tr><td>Drive Away Price<sup>*</sup></td><td>{car.driveAwayPrice}</td></tr>
                              <tr><td>Net amount financed</td><td>{car.netAmountFinanced}</td></tr>
                            </tbody></table>
                            <div className="ctas">
                              <Button
                                variant="secondary"
                                theme={theme === "dark" ? "light" : null}
                                onClick={()=>{
                                  setSelectedResult(car);
                                  setShowApplyModal(true);
                                  gtmPush("fbcFinanceEnquiryCTA", "FinanceBudgetCalculator", car.variantName);
                                }}
                              >
                                Finance enquiry
                              </Button>
                              <Button
                                variant="primary"
                                theme={theme === "dark" ? "light" : null}
                                onClick={()=>{
                                  setSelectedResult(car);
                                  setShowEnquiry(true);
                                  gtmPush("fbcVehicleEnquiryCTA", "FinanceBudgetCalculator", car.variantName);
                                }}
                              >
                                Vehicle enquiry
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          
        </div>}

        <EnquiryForm
          queryString={query}
          locPostcode={locPostcode}
          show={showEnquiry && selectedResult !== null}
          theme={theme}
          onCancel={()=>{
            setSelectedResult(null);
            setShowEnquiry(false);
            gtmPush("fbcCancelClicked", "FinanceBudgetCalculatorEnquiry");
          }}
          data={{
            formName: "Budget Calculator",
            vehicleImageUrl: selectedResult?.imageUrl,
            modelName: selectedResult?.variantName,
            engine: selectedResult?.engineName,
            loanType: financeValues?.purpose,
            loanOption: "",
            disclaimer: "",
            description: "",
            vehiclePrice: selectedResult?.driveAwayPrice,
            repayment: selectedResult?.weeklyRepayments,
            repaymentLabel: "Weekly repayments",
            netAmountFinanced: selectedResult?.netAmountFinanced,
            comparisonRate: `${financeValues?.interestRate}%`,
            loanTerm: financeValues?.term,
            deposit: financeValues?.deposit,
            kmLimit: "",
            gfv: ""
          }}
          thankyouDisclaimer={terms?.productDisclaimer}
          repaymentSymbol="#"
          trackingFormName="FinanceBudgetCalculatorEnquiry"
          trackingPrefix="fbc"
        />

        <ApplyFormModal
          isActive={showApplyModal && selectedResult !== null}
          theme={theme}
          close={() => {
            setSelectedResult(null);
            setShowApplyModal(false);
          }}
          variant={{name: selectedResult?.variantName}}
          engine={{name: selectedResult?.engineName}}
          image={selectedResult?.imageUrl}
          interestRate={financeValues?.interestRate}
          price={selectedResult?.driveAwayPrice}
          term={financeValues?.term}
          downPayment={financeValues?.deposit}
          repayment={selectedResult?.weeklyRepayments}
          balloon={selectedResult?.finalBalloonRepayments || selectedResult?.fianlBalloonRepayments}
          disclaimer={budgetCalcDisclaimer}
          description={""}
          loanType={financeValues?.purpose}
          onEnterPostcode={(pc) => { gtmPush("fbcFinanceEnquiryPostcodeEntered", "FinanceBudgetCalculator", pc); }}
          onSelectDealer={(d) => { gtmPush("fbcFinanceEnquiryDealerSelected", "FinanceBudgetCalculator", d.dealer.name); }}
          onSuccess={()=>{ gtmPush("fbcFinanceEnquiryFinalSelectSuccess", "FinanceBudgetCalculator"); }}
          calculatorLabel="Audi Budget calculator"
        />
        
        <PostcodeModal isActive={!locState} theme={theme} onSubmit={(s,p) => {
          setLocState(s); setLocPostcode(p);
          if (hasTrackedPostcode) {
            gtmPush("fbcPostcodeChanged", "FinanceBudgetCalculator", p);
          } else {
            gtmPush("fbcPostcodeLoaded", "FinanceBudgetCalculator", p);
            setHasTrackedPostcode(true);
          }
        }} />
      </main>
    </Layout>
  );
}

export default BudgetCalculator;
